import { type BrandProfile, DEFAULT_BRAND_PROFILE } from '../types'

// ─── Hardcoded brand profiles for beta clubs ─────────────────────────────────

const SEVEN_MILE_BEACH_BRAND: BrandProfile = {
  clubName: '7 Mile Beach',
  skin: 'default',
  theme: 'light',
  colors: {
    primary: '#5e7d5d',
    accent: '#fcb040',
    background: '#FEF8F1',
    text: '#262626',
    buttonText: '#ffffff',
    error: '#f85149',
  },
  typography: {
    fontFamily: "'Gilroy', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFontFamily: "'Gilroy', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    borderRadius: '50px',
  },
  assets: {
    logoUrl: 'https://www.7milegolf.com.au/wp-content/uploads/2025/07/7MB-Emblem.png',
    logoDarkUrl: 'https://www.7milegolf.com.au/wp-content/uploads/2025/07/7MB-Monotone-DBG.png',
  },
  button: {
    text: 'Book a Tee Time',
    position: 'bottom-right',
    style: 'pill',
    icon: true,
  },
  customCSS: '',
  features: {
    showPoweredBy: true,
    mobileBehaviour: 'fullscreen',
    currency: 'AUD',
    locale: 'en-AU',
  },
}

/** Map of clubId → hardcoded brand profile for beta testing */
const BETA_PROFILES: Record<string, BrandProfile> = {
  '7mb': SEVEN_MILE_BEACH_BRAND,
  '7mile-beach': SEVEN_MILE_BEACH_BRAND,
}

const CACHE_KEY = 'currents_brand_profile'
const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hour

interface CachedProfile {
  profile: BrandProfile
  timestamp: number
  clubId: string
}

/**
 * Get the API base URL for the given environment.
 */
function getApiBase(env: string): string {
  return env === 'staging'
    ? 'https://staging.api.currents.golf'
    : 'https://api.currents.golf'
}

/**
 * Try to read a cached brand profile from localStorage.
 * Returns null if no cache, expired, or different clubId.
 */
function getCachedProfile(clubId: string): BrandProfile | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null

    const cached: CachedProfile = JSON.parse(raw)
    if (cached.clubId !== clubId) return null
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) return null

    return cached.profile
  } catch {
    return null
  }
}

/**
 * Store a brand profile in localStorage with timestamp.
 */
function setCachedProfile(clubId: string, profile: BrandProfile): void {
  try {
    const cached: CachedProfile = {
      profile,
      timestamp: Date.now(),
      clubId,
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached))
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

/**
 * Fetch the brand profile for a club from the Currents API.
 *
 * Resolution order:
 * 1. localStorage cache (if valid and not expired)
 * 2. API fetch
 * 3. Default fallback (widget always renders something)
 */
export async function fetchBrandProfile(
  clubId: string,
  env: string = 'production'
): Promise<BrandProfile> {
  // 0. Check for hardcoded beta profiles
  const betaProfile = BETA_PROFILES[clubId]
  if (betaProfile) return betaProfile

  // 1. Check cache
  const cached = getCachedProfile(clubId)
  if (cached) return cached

  // 2. Fetch from API
  try {
    const base = getApiBase(env)
    const res = await fetch(`${base}/api/v1/clubs/${clubId}/brand-profile`, {
      headers: { Accept: 'application/json' },
    })

    if (!res.ok) {
      console.warn(`[Currents] Brand profile fetch failed: ${res.status}`)
      return DEFAULT_BRAND_PROFILE
    }

    const profile: BrandProfile = await res.json()
    setCachedProfile(clubId, profile)
    return profile
  } catch (err) {
    console.warn('[Currents] Brand profile fetch error:', err)
    return DEFAULT_BRAND_PROFILE
  }
}
